import { ReactNode, useMemo } from "react";
import { TrashIcon } from "lucide-react";
import { Button } from "@/components/Base/Button";
import { MultiSelect } from "@/components/Base/MultiSelect";
import { Select } from "@/components/Common/Select";
import { Tooltip } from "@/components/Common/Tooltip";
import { Option } from "@/types/v2/UI";
import {
  encodeScheduleTriggerValue,
  formatScheduleTriggerValue,
  isScheduleValueComplete,
  parseScheduleTriggerValue,
  ScheduleCadence,
  ScheduleTriggerValue,
  WEEKDAY_KEYS,
  WEEKDAY_LABELS,
  WeekdayKey,
} from "@/lib/automationSchedule";

const CADENCE_OPTIONS: Option[] = [
  { label: "Every day", value: "daily" },
  { label: "Every weekday", value: "weekdays" },
  { label: "On selected days", value: "days" },
];

const DAY_OPTIONS: Option[] = WEEKDAY_KEYS.map((d) => ({
  value: d,
  label: WEEKDAY_LABELS[d],
}));

export interface ScheduleValueProps {
  label: string;
  value: string | number | boolean | null;
  onChange?: (raw: string) => void;
  onRemove?: () => void;
  canRemove?: boolean;
  /**
   * Whether this row offers the "add a condition" affordance. Mirrors
   * `Trigger`'s prop of the same name: the schedule row replaces `<Trigger>`
   * in the builder, so without this a schedule trigger is the only trigger in
   * the catalog whose conditionals cannot be reached from the UI at all.
   */
  canAdd?: boolean;
  /** The conditions `<BuilderDialog>`, rendered next to the remove button. */
  addComponent?: ReactNode;
  readOnly?: boolean;
  disabled?: boolean;
}

export const ScheduleValue = ({
  label,
  value,
  onChange,
  onRemove,
  canRemove = true,
  canAdd = false,
  addComponent,
  readOnly,
  disabled,
}: ScheduleValueProps) => {
  const parsed = useMemo(() => parseScheduleTriggerValue(value), [value]);
  const isReadOnly = readOnly || !onChange;

  const emit = (next: ScheduleTriggerValue) =>
    onChange?.(encodeScheduleTriggerValue(next));

  if (isReadOnly) {
    return (
      <div
        className={"flex flex-row items-center gap-2 text-base text-primary"}
      >
        <span>{label}:</span>
        <span>{formatScheduleTriggerValue(parsed)}</span>
      </div>
    );
  }

  return (
    <div
      data-testid={"schedule-card"}
      className={"flex flex-col gap-3 rounded-lg border border-border p-3"}
    >
      <div className={"flex flex-row items-center justify-between"}>
        <span className={"text-base text-primary"}>{label}</span>
        {(canAdd || (canRemove && onRemove)) && (
          <div className={"flex flex-row gap-2"}>
            {canAdd && addComponent}
            {canRemove && onRemove && (
              <Tooltip message={"Remove"}>
                <Button
                  type={"button"}
                  size={"icon"}
                  variant={"destructive-alt"}
                  aria-label={"Remove schedule"}
                  onClick={onRemove}
                >
                  <TrashIcon />
                </Button>
              </Tooltip>
            )}
          </div>
        )}
      </div>

      <div
        className={
          "flex flex-row flex-wrap items-center gap-2 text-base text-primary"
        }
      >
        <Select
          selectTriggerProps={{ variant: "minimal" }}
          disabled={disabled}
          value={parsed.cadence}
          options={CADENCE_OPTIONS}
          onValueChange={(v) =>
            emit({ ...parsed, cadence: v as ScheduleCadence })
          }
        />

        {parsed.cadence === "days" && (
          <div data-testid={"schedule-days"}>
            <MultiSelect
              variant={"minimal"}
              disabled={disabled}
              options={DAY_OPTIONS}
              value={parsed.days}
              itemsName={"days"}
              placeholder={"Select days"}
              onChange={(days) =>
                emit({ ...parsed, days: days as WeekdayKey[] })
              }
            />
          </div>
        )}

        <span>{"at"}</span>
        <input
          aria-label={"Time"}
          type={"time"}
          disabled={disabled}
          value={parsed.time}
          onChange={(e) => emit({ ...parsed, time: e.target.value })}
          className={
            "border-b border-input bg-background px-2 py-1 text-primary focus:outline-none"
          }
        />

        {!isScheduleValueComplete(parsed) && (
          <span className={"text-sm text-destructive"}>
            {"Select at least one day"}
          </span>
        )}
      </div>
    </div>
  );
};
